import React, { useEffect, useRef, useState } from 'react';
import { GRANT_LEVEL_LABEL, GRANT_LEVEL_DESCRIPTION } from '@discovr/api';
import type { GrantLevel } from '@discovr/api';
import { Check, FunnelSimple, Trash } from '../../design-system/icons';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Input } from '../ui/input';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../ui/popover';
import { LoadingSpinner } from './LoadingSpinner';

const LEVELS: GrantLevel[] = ['read', 'write', 'admin'];
// New campaigns default to Editor, not Viewer — matches the Figma mock's
// example rows; the picker has no "default level" control, so this is the
// only place a fresh selection's level comes from before a per-row override.
const NEW_SELECTION_LEVEL: GrantLevel = 'write';

export interface CampaignAccessSelection {
  campaignId: string;
  campaignName: string;
  level: GrantLevel;
}

export interface CampaignAccessPickerProps {
  /** Pre-fetched by the caller — brand app passes its own campaigns, admin
   *  app passes all campaigns with brand name. */
  campaigns: { id: string; name: string; brandName?: string }[];
  value: CampaignAccessSelection[];
  onChange: (next: CampaignAccessSelection[]) => void;
  /** True while the caller's campaign list is still being fetched — renders
   *  a loading state in place of the search input and the "no campaigns
   *  yet" message, so an empty `campaigns` array isn't mistaken for a brand
   *  or org that genuinely has zero campaigns. */
  isLoading?: boolean;
  /** Portal target for the search dropdown — pass the enclosing Dialog/Sheet's
   *  content element when this picker renders inside one (see
   *  ShopifyProductPicker's `portalContainer`), so the dropdown portals INSIDE
   *  it instead of to `document.body`, which Radix's Dialog would otherwise
   *  treat as an outside click and close mid-selection. */
  portalContainer?: HTMLElement | null;
}

// A level Select's option list — label + description caption per row,
// matching AccessControlTab's two-line SelectItem pattern. Radix mirrors an
// option's full children (label + description) into the closed trigger via
// SelectValue, which only fits in a wide container. `compact` renders just
// the label in the closed trigger instead — for the narrow per-row selects,
// where the two-line mirror overflowed its column — while the open dropdown
// still shows every option's label + description either way.
export function LevelSelect({
  value,
  onChange,
  ariaLabel,
  compact = false,
}: {
  value: GrantLevel;
  onChange: (level: GrantLevel) => void;
  ariaLabel?: string;
  compact?: boolean;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as GrantLevel)}>
      <SelectTrigger size="sm" aria-label={ariaLabel}>
        {compact ? GRANT_LEVEL_LABEL[value] : <SelectValue />}
      </SelectTrigger>
      {/* item-aligned (Radix's default) mis-measures this content's two-line
          items and renders the panel far off-screen — popper positioning
          anchors to the trigger via the viewport instead. */}
      <SelectContent position="popper" align="start" sideOffset={4}>
        {LEVELS.map((l) => (
          <SelectItem key={l} value={l} className="py-2">
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-body-sm-medium text-foreground">{GRANT_LEVEL_LABEL[l]}</span>
              <span className="text-caption-regular text-muted-foreground">{GRANT_LEVEL_DESCRIPTION[l]}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Single-select "Brand: X" filter — radio list, "All brands" plus every
// distinct brandName found in `campaigns`. Narrows the search list only;
// it has no bearing on what's already selected. The caller only renders this
// when there are 2+ distinct brands (see CampaignAccessPicker) — a
// single-brand workspace (the brand app) has nothing to filter by.
function BrandFilter({
  brands,
  value,
  onChange,
  portalContainer,
}: {
  brands: string[];
  value: string | null;
  onChange: (brand: string | null) => void;
  portalContainer?: HTMLElement | null;
}) {
  const [open, setOpen] = useState(false);
  // Fixed width sized to the longest label (brand name or "All brands"),
  // clamped to a sane range — otherwise the trigger resizes as the selection
  // changes, shifting the search box beside it.
  const longestLabel = Math.min(
    24,
    Math.max(10, 'All brands'.length, ...brands.map((b) => b.length)),
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          style={{ width: `calc(${longestLabel}ch + 2.75rem)` }}
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-input px-3 text-left text-body-sm-regular text-foreground hover:bg-muted"
        >
          <FunnelSimple size={14} className="shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-left">{value ?? 'All brands'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        container={portalContainer}
        className="w-56 max-h-72 overflow-y-auto rounded-md border bg-popover p-1"
      >
        {[null, ...brands].map((b) => {
          const checked = value === b;
          return (
            <button
              key={b ?? '__all__'}
              type="button"
              role="radio"
              aria-checked={checked}
              onClick={() => { onChange(b); setOpen(false); }}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-muted"
            >
              <span
                className={`grid size-[16px] shrink-0 place-content-center rounded-full border transition-colors ${
                  checked ? 'border-(--brand-500)' : 'border-input'
                }`}
              >
                {checked && <span className="size-2 rounded-full bg-(--brand-500)" />}
              </span>
              <span className="min-w-0 flex-1 truncate text-body-sm-regular text-foreground">{b ?? 'All brands'}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Search-and-pick for campaigns — same interaction as ShopifyProductPicker's
 * product search: the dropdown opens on focus (not a separate trigger
 * button), lists every campaign client-side-filtered by name/brand, and each
 * row is a checkbox that toggles without closing the list. Selected campaigns
 * render separately, as the reserved box below (see CampaignAccessPicker).
 */
function CampaignSearchPicker({
  campaigns,
  brandFilter,
  selectedIds,
  onToggle,
  portalContainer,
}: {
  campaigns: { id: string; name: string; brandName?: string }[];
  brandFilter: string | null;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  portalContainer?: HTMLElement | null;
}) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  // Delay the blur-close so a result row's onClick still lands before the
  // list unmounts; onFocus below cancels a pending close on fast refocus.
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
  }, []);

  const focusFirstResult = () => {
    listRef.current?.querySelector<HTMLButtonElement>('button[role="checkbox"]')?.focus();
  };

  const query = term.trim().toLowerCase();
  const matches = campaigns.filter(
    (c) =>
      (!brandFilter || c.brandName === brandFilter) &&
      (!query || c.name.toLowerCase().includes(query) || (c.brandName ?? '').toLowerCase().includes(query)),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="min-w-0 flex-1">
          <Input
            value={term}
            placeholder="Search campaigns…"
            aria-label="Search campaigns"
            onChange={(e) => setTerm(e.target.value)}
            onFocus={() => {
              if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                focusFirstResult();
              }
            }}
            onBlur={() => {
              blurTimeoutRef.current = setTimeout(() => setOpen(false), 150);
            }}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        ref={listRef}
        align="start"
        sideOffset={4}
        container={portalContainer}
        onOpenAutoFocus={(e) => e.preventDefault()}
        // The search input is a PopoverAnchor, not a Radix-exempt
        // PopoverTrigger — guard it manually so typing doesn't dismiss the list.
        onInteractOutside={(e) => {
          if (anchorRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
        className="max-h-64 w-(--radix-popover-trigger-width) overflow-auto rounded-md border bg-popover p-1"
      >
        {matches.length === 0 ? (
          <div className="px-3 py-2 text-body-sm-regular text-muted-foreground">
            {query || brandFilter ? 'No matching campaigns.' : 'No campaigns.'}
          </div>
        ) : (
          matches.map((c) => {
            const checked = selectedIds.has(c.id);
            return (
              <button
                key={c.id}
                type="button"
                role="checkbox"
                aria-checked={checked}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onToggle(c.id)}
                className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left hover:bg-muted"
              >
                <span
                  className={`grid size-[18px] shrink-0 place-content-center rounded-[5px] border transition-colors ${
                    checked ? 'border-(--brand-500) bg-(--brand-500) text-white' : 'border-input'
                  }`}
                >
                  {checked && <Check size={12} weight="bold" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-body-sm-regular text-foreground">{c.name}</span>
                  {c.brandName && (
                    <span className="block truncate text-caption-regular text-muted-foreground">{c.brandName}</span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * CampaignAccessPicker — a multi-select for assigning per-campaign access as
 * part of a two-step invite flow (brand InviteDialog, admin InviteForm) and
 * MemberAccessDrawer's "Add campaigns" step. A `Brand: X` filter (hidden when
 * the campaign list only spans one brand) sits next to a search box that
 * opens a checkbox dropdown on focus. Selected campaigns render below in a
 * fixed-height reserved box — present from the first render (not just once
 * something is selected), showing placeholder copy when empty — so the
 * surrounding dialog never resizes as selections are made.
 */
export function CampaignAccessPicker({
  campaigns,
  value,
  onChange,
  isLoading = false,
  portalContainer,
}: CampaignAccessPickerProps) {
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const brands = Array.from(new Set(campaigns.map((c) => c.brandName).filter((b): b is string => !!b))).sort();

  const selectedIds = new Set(value.map((v) => v.campaignId));
  const campaignsById = new Map(campaigns.map((c) => [c.id, c]));

  const toggleCampaign = (campaignId: string) => {
    if (selectedIds.has(campaignId)) {
      onChange(value.filter((v) => v.campaignId !== campaignId));
      return;
    }
    const c = campaignsById.get(campaignId);
    if (!c) return;
    onChange([...value, { campaignId: c.id, campaignName: c.name, level: NEW_SELECTION_LEVEL }]);
  };

  const removeCampaign = (campaignId: string) => onChange(value.filter((v) => v.campaignId !== campaignId));

  const changeLevel = (campaignId: string, level: GrantLevel) =>
    onChange(value.map((v) => (v.campaignId === campaignId ? { ...v, level } : v)));

  // The reserved box shows whenever there's something to put in it: either a
  // real selection (even mid-load — a demotion review pre-fills `value`
  // before `campaigns` necessarily finishes fetching) or, once loaded, the
  // empty-state placeholder alongside the search UI it sits under.
  const showSelectedBox = value.length > 0 || (!isLoading && campaigns.length > 0);

  return (
    <div className="flex flex-col gap-3">
      {isLoading ? (
        <LoadingSpinner size={24} className="text-muted-foreground" />
      ) : campaigns.length === 0 ? (
        <p className="text-body-sm-regular text-muted-foreground">
          No campaigns yet — you can assign access later from Team settings.
        </p>
      ) : (
        <div className="flex gap-2">
          <CampaignSearchPicker
            campaigns={campaigns}
            brandFilter={brandFilter}
            selectedIds={selectedIds}
            onToggle={toggleCampaign}
            portalContainer={portalContainer}
          />
          {brands.length > 1 && (
            <BrandFilter brands={brands} value={brandFilter} onChange={setBrandFilter} portalContainer={portalContainer} />
          )}
        </div>
      )}

      {showSelectedBox && (
        // h-80 — matches AllCampaignsAccessPreview's box (the Admin-tier
        // stand-in for this whole search+box), so the two states' total
        // heights land close enough that MIN_CAMPAIGN_SECTION_HEIGHT's floor
        // doesn't leave a visible gap above the footer in either one.
        <div className="h-80 min-w-0 overflow-y-auto rounded-lg border border-border">
          {value.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-1 px-6 text-center">
              <p className="text-body-sm-regular text-muted-foreground">Selected campaigns will show here</p>
              {campaigns.length > 0 && (
                <p className="text-caption-regular text-muted-foreground">
                  They won&apos;t see any campaigns until you assign some.
                </p>
              )}
            </div>
          ) : (
            <ul>
              {value.map((v) => (
                // Border on every row, including the last — the box's own
                // fixed height (h-80) usually leaves empty space below the
                // final campaign, so a `divide-y`-style gap-only border
                // would leave that last row visually unclosed against it.
                <li key={v.campaignId} className="flex items-center justify-between gap-3 border-b border-border px-4 py-2">
                  <div className="min-w-0 truncate text-body-sm-semibold text-foreground">{v.campaignName}</div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <div className="w-32">
                      <LevelSelect
                        value={v.level}
                        onChange={(level) => changeLevel(v.campaignId, level)}
                        ariaLabel={`Access level for ${v.campaignName}`}
                        compact
                      />
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove ${v.campaignName}`}
                      onClick={() => removeCampaign(v.campaignId)}
                      className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
